import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Card,
  CardMedia,
  CardContent,
  IconButton,
  Chip,
  styled,
  useMediaQuery,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { getColorForIndex, thumbnailView } from "../../utilities";
import { ThumbsUp, Heart } from "@phosphor-icons/react";
import Ratings from "../../components/common/Ratings";
import { useTheme } from "@emotion/react";
import { motion } from "framer-motion";
import CircleIcon from "@mui/icons-material/Circle";
import useAddToCart from "../../hooks/useAddToCart";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

const Tag = styled(Box)(({ theme }) => ({
  textTransform: "capitalize",
  fontWeight: "500",
  fontSize: "14px",
  lineHeight: "125%",
  color: "var(--primary-red-300)",
  textAlign: "left",
  background: "var(--primary-red-100)",
  padding: "2px 12px",
  borderRadius: "143px",
  width: "fit-content",
  marginTop: "8px",
  alignItems: "center",
}));

const MotionCard = motion(Card);
const MotionCardMedia = motion(CardMedia);

function ProductItem({ item, favorites, index, navigate }) {
  const { handleAddToCart } = useAddToCart();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isHovering, setIsHovering] = useState(false);

  // Memoize images array to prevent recreation on every render
  const allImages = useMemo(() => {
    const defaultImageUrl = thumbnailView(item?.app_image?.app_product_image);
    const additionalImages = item?.first_image || [];

    let images = [];

    // If first_image array exists and has images, use only those (don't include app_product_image)
    if (additionalImages.length > 0) {
      images = additionalImages.map((img) => thumbnailView(img.product_image));
    } else {
      // If no first_image, use app_product_image
      images = [defaultImageUrl];
    }

    // Filter out any undefined/null/empty images
    return images.filter(Boolean);
  }, [item?.app_image?.app_product_image, item?.app_image?.first_image]);

  const hasMultipleImages = allImages.length > 1;
  const defaultImageUrl = allImages[0];

  // console.log('ProductItem for', item?.product_name, 'allImages:', allImages, 'hasMultipleImages:', hasMultipleImages);

  // Automatic image sliding on hover - only for the image area
  useEffect(() => {
    if (!isHovering || !hasMultipleImages) {
      return;
    }

    // console.log('Starting interval for', item?.product_name);

    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % allImages.length);
    }, 2000); // Change image every 2 seconds

    return () => {
      clearInterval(interval);
    };
  }, [isHovering, hasMultipleImages, allImages, item?.product_name]);

  // Handle hover on image only
  const handleImageMouseEnter = (e) => {
    e.stopPropagation();
    setIsHovering(true);
  };

  const handleImageMouseLeave = (e) => {
    e.stopPropagation();
    setIsHovering(false);
    setCurrentImageIndex(0);
  };

  // Determine which image to show
  const currentImageUrl = allImages[currentImageIndex] || allImages[0];

  const productName = item?.product_name;
  const flavour = item?.flavour[0]?.itemName;
  const ratings = parseFloat(item?.ratings);
  const comboPrice = item?.combo_price;
  const photoCakes = item?.shape_id;

  let discountPrice = 0;
  let originalPrice = 0;
  let discountPercent = 0;
  let minimumQty = 0;

  const bulkPrice = item?.bulk?.bulk_price || [];

  if (bulkPrice?.length > 0) {
    const leastPriceObject = bulkPrice.reduce((min, current) => {
      const currentEffectivePrice =
        +current.egg_price > 0
          ? +current.egg_price
          : +current.eggless_price || Infinity;
      const minEffectivePrice =
        +min.egg_price > 0 ? +min.egg_price : +min.eggless_price || Infinity;
      return currentEffectivePrice < minEffectivePrice ? current : min;
    }, bulkPrice[0]);

    discountPrice =
      +leastPriceObject.egg_price > 0
        ? leastPriceObject.egg_price
        : leastPriceObject.eggless_price;
    originalPrice = discountPrice;

    const minimumQtyArray = bulkPrice?.map((item) => item?.min_quantity);
    minimumQty = Math.min(...minimumQtyArray) || 0;
  } else {
    discountPrice =
      +item?.variation[0]?.eggOriginalPrice > 0
        ? item?.variation[0]?.eggOriginalPrice
        : item?.variation[0]?.eggLessOriginalPrice;

    originalPrice =
      +item?.variation[0]?.egg > 0
        ? item?.variation[0]?.egg
        : item?.variation[0]?.eggLess;

    if (originalPrice > 0 && (!comboPrice || comboPrice === 0)) {
      discountPercent = item?.variation[0]?.discountPercent || 0;

      if (discountPercent === 0) {
        discountPrice = originalPrice;
      }
    } else {
      discountPercent = 0;
    }
  }

  let preparationHours = item?.variation[0]?.egg_preparation;
  if (preparationHours == 0) {
    preparationHours = item?.variation[0]?.eggless_preparation;
  }

  const bestSeller = item?.product_tags?.best_selling == 1;
  const newArrival = item?.product_tags?.new_product == 1;

  // Handle dot click
  const handleDotClick = (e, imageIndex) => {
    e.stopPropagation(); // Prevent card click
    setCurrentImageIndex(imageIndex);
  };
  const isNavigate = photoCakes || comboPrice > 0 || bulkPrice?.length > 0;
  return (
    <MotionCard
      onClick={() =>
        navigate(`/product-description/${item?.product_name}/${item?.id}`, {
          state: { fallbackImage: defaultImageUrl },
        })
      }
      sx={{
        position: "relative",
        background: "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(250,246,239,0.95) 100%)",
        border: "1px solid rgba(206, 177, 119, 0.18)",
        fontSize: { xs: "10px", sm: "12px" },
        boxShadow: "0 1px 4px 0 rgba(12, 12, 13, 0.04)",
        cursor: "pointer",
        borderTop: "3px solid transparent",
        transition: "transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: "0 8px 24px rgba(196, 154, 39, 0.12)",
          borderTop: "3px solid rgba(183,123,26,0.60)",
        },
      }}
    >
      {/* Best Seller Chip */}
      {bestSeller && (
        <Chip
          icon={
            <ThumbsUp size={isMobile ? 12 : 16} color="#6B4A05" weight="fill" />
          }
          label={"Featured"}
          size="small"
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            fontSize: { xs: "8px", sm: "12px" },
            background: "linear-gradient(135deg, #F6E7BD 0%, #EBCF86 100%)",
            fontWeight: 700,
            color: "#6B4A05",
            borderRadius: "4px 0px 4px 0",
            padding: { xs: "1px", sm: "6px 8px" },
            minWidth: { xs: "auto", sm: "90px" },
            zIndex: 2,
            border: "1px solid rgba(183,123,26,0.18)",
          }}
        />
      )}

      {/* New Arrival Chip */}
      {newArrival && (
        <Chip
          icon={
            <Heart size={isMobile ? 12 : 16} color="#6B4A05" weight="fill" />
          }
          label={"New"}
          size="small"
          sx={{
            position: "absolute",
            top: 0,
            right: 0,
            fontSize: { xs: "8px", sm: "12px" },
            background: "linear-gradient(135deg, #F7F1E7 0%, #EAD7B3 100%)",
            fontWeight: 700,
            color: "#6B4A05",
            borderRadius: "0px 4px 0px 4px ",
            padding: { xs: "1px", sm: "6px" },
            border: "1px solid rgba(183,123,26,0.18)",
            zIndex: 2,
          }}
        />
      )}

      {/* Product Image Container */}
      <Box
        sx={{ position: "relative" }}
        onMouseEnter={handleImageMouseEnter}
        onMouseLeave={handleImageMouseLeave}
      >
        <MotionCardMedia
          layoutId={`card-${item?.id}`}
          component="img"
          image={currentImageUrl}
          alt={productName}
          sx={{
            objectFit: "contain",
            background: "radial-gradient(circle at top, rgba(255,255,255,0.96) 0%, rgba(242,235,223,0.80) 48%, rgba(235,226,208,0.72) 100%)",
            height: {
              xs: "calc((100vw - 48px - 12px) / 2)",
              sm: "200px",
              lg: "240px",
            },
            p: 1,
          }}
        />
        {(item?.is_best_seller === 1 ||
          item?.is_customizable === 1 ||
          item?.is_featured === 1) && (
          <Box
            sx={{
              position: "absolute",
              bottom: 0,
              right: 0,
              zIndex: 3,
            }}
          >
            <Chip
              label="Curated"
              size="small"
              sx={{
                fontSize: { xs: "9px", sm: "12px" },
                background: "linear-gradient(135deg, #C59A42 0%, #DAB76B 100%)",
                fontWeight: 700,
                color: "white",
                borderRadius: "4px 0px 0px 0px",
                padding: { xs: "8px 8px 8px 6px", sm: "6px 10px" },
                minWidth: { xs: "auto", sm: "90px" },
                boxShadow: "0 10px 18px rgba(183, 123, 26, 0.14)",
              }}
            />
          </Box>
        )}

        {/* Image Indicator Dots - Only show if multiple images and hovering */}
        {hasMultipleImages && (
          <Box
            sx={{
              position: "absolute",
              bottom: 8,
              left: "50%",
              transform: "translateX(-50%)",
              display: "flex",
              gap: 0.5,
              zIndex: 2,
              // backgroundColor: "rgba(0, 0, 0, 0.3)",
              borderRadius: "20px",
              padding: "4px 8px",
            }}
          >
            {allImages.map((_, imgIndex) => (
              <CircleIcon
                key={imgIndex}
                onClick={(e) => handleDotClick(e, imgIndex)}
                sx={{
                  fontSize: 10,
                  color: currentImageIndex === imgIndex ? "#C49A27" : "#fff",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    color: "#C49A27",
                    transform: "scale(1.2)",
                  },
                }}
              />
            ))}
          </Box>
        )}
      </Box>

      {/* Card Content */}
      <CardContent
        sx={{
          p: { xs: 1.5, sm: 2 },
          "&:last-child": {
            paddingBottom: "12px",
            height: "auto", // ✅ REMOVE fixed height completely
            minHeight: { xs: "110px", sm: "130px", md: "auto" }, // ✅ use minHeight instead
          },
        }}
      >
        {/* Product Name */}
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: { xs: "12px", sm: "18px" },
            color: "var(--neutral-800)",
            textAlign: "left",
            lineHeight: "150%",
            textTransform: "capitalize",
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: { xs: 2, sm: 2 },
            WebkitBoxOrient: "vertical",
            wordBreak: "break-word",
            height: { xs: "36px", sm: "54px" },
          }}
        >
          {productName}
        </Typography>

        {/* Ratings */}
        <Box
          display="flex"
          alignItems="center"
          justifyContent={"space-between"}
          mt={{ xs: 0.5, sm: 1 }}
        >
          <Ratings value={ratings} />
        </Box>

        {/* Price Section */}
        <Box
          display="flex"
          alignItems="center"
          justifyContent={"space-between"}
          sx={{ mt: { xs: 1, sm: 2 } }}
        >
          <Box
            sx={{
              gap: { xs: 0, sm: 1 },
              flexWrap: "wrap",
              width: "100%",
              justifyContent: { xs: "space-between", sm: "flex-start" },
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
            }}
          >
            {/* Combo Price */}
            {comboPrice > 0 && (
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: "16px", md: "20px", lg: "26px" },
                  lineHeight: "125%",
                  color: "var(--neutral-black)",
                  textAlign: "left",
                }}
              >
                ₹{comboPrice}
              </Typography>
            )}

            {/* Discounted Price */}
            {comboPrice <= 0 && discountPrice && (
              <Typography
                fontWeight="600"
                sx={{
                  fontSize: { xs: "14px", sm: "18px", md: "22px" },
                  textDecoration: comboPrice > 0 ? "line-through" : "none",
                  color:
                    comboPrice > 0
                      ? "var(--neutral-700)"
                      : "var(--neutral-black)",
                  marginLeft: comboPrice > 0 ? "25px" : 0,
                }}
              >
                ₹{discountPrice}
              </Typography>
            )}

            {/* Original Price (only if no combo) */}
            {originalPrice !== discountPrice && (
              <Typography
                color="var(--neutral-700)"
                fontWeight={500}
                sx={{
                  textDecoration: "line-through",
                  ml: { xs: 0.6, sm: 1 },
                  lineHeight: "120%",
                  fontSize: { xs: "13px", sm: "16px", md: "20px" },
                }}
              >
                ₹{originalPrice}
              </Typography>
            )}

            {/* Discount % (only if no combo) */}
            {comboPrice <= 0 && discountPercent > 0 && (
              <Typography
                fontWeight={500}
                sx={{
                  ml: { xs: 0.6, sm: 1 },
                  lineHeight: "120%",
                  fontSize: { xs: "10px", sm: "11px", md: "12px" },
                  color: "#0cb67b",
                  border: "1px solid #0cb67b",
                  padding: "2px 4px",
                  borderRadius: "4px",
                  backgroundColor: "rgba(8, 177, 118, 0.1)",
                }}
              >
                {discountPercent}% Off
              </Typography>
            )}
          </Box>

          {/* Arrow Button */}
          {/* <IconButton
            color="inherit"
            size="small"
            aria-label="arrow_forward"
            onClick={(e) => handleAddToCart(e, item)}
            sx={{
              alignSelf: "center",
              backgroundColor: "var(--primary-red-base)",
              borderRadius: "8px",
              display: { xs: "none", sm: "flex" },
            }}
            rel="noopener noreferrer"
          >
            <ArrowForwardIcon
              sx={{ fontSize: { xs: 16, sm: 24, lg: 30 }, color: "white" }}
            />
          </IconButton> */}
          {/* Arrow / Add Button */}
          <IconButton
            color="inherit"
            size="small"
            aria-label="action_button"
            onClick={(e) => {
              if (isNavigate) {
                // ✅ bulk or combo → navigate to product page
                navigate(
                  `/product-description/${item?.product_name}/${item?.id}`,
                  {
                    state: { fallbackImage: defaultImageUrl },
                  },
                );
              } else {
                // ✅ normal product → add to cart
                handleAddToCart(e, item);
              }
            }}
            sx={{
              alignSelf: "center",
              backgroundColor: "var(--primary-red-base)",
              borderRadius: "8px",
              display: { xs: "none", sm: "flex" },
              "&:hover": {
                backgroundColor: "var(--primary-red-500)", 
              },
            }}
          >
            {isNavigate ? (
              <ArrowForwardIcon // ✅ arrow for bulk/combo
                sx={{ fontSize: { xs: 16, sm: 24, lg: 30 }, color: "white" }}
              />
            ) : (
              <AddIcon // ✅ + for normal
                sx={{ fontSize: { xs: 16, sm: 24, lg: 30 }, color: "white" }}
              />
            )}
          </IconButton>
        </Box>
      </CardContent>
    </MotionCard>
  );
}

export default ProductItem;
